import {
  getCapturedNotes,
  getNoteCacheKey,
  hasNoteStorage,
  markCapturedNoteSignedOff,
  saveCapturedNote
} from './NoteStore.ts';
import { flushDueEntries } from './NoteOffload.ts';

/*
Event-driven, section-level note capture: the alternative to the polling in
NoteCapture.ts for injectors that export `noteFocusCapture` (Stride, Prompt).

Each injector supplies a FocusCaptureSource that only reads the page: which
section a node belongs to, which sections are rendered, and the values of one
section. Everything else is here, shared:

- A section is read on focusout, whether or not its value changed.
- A section is marked dirty by a DOM mutation inside it (toggle buttons,
  a menu pick, a date chosen from a calendar, rows added or removed).
- An input event outside the focused section also marks it dirty: setting a
  field's value programmatically need not cause a DOM mutation or a blur.
- A dirty section that does not hold focus (a fill from the panel, a change
  made while the side panel has focus) is read once the note has been quiet for
  DEBOUNCE_MS, or after MAX_WAIT_MS if it never goes quiet.
- When a note opens, every rendered section is read once, and a section that
  renders later is read once it does, so values from an earlier session are
  captured even if nothing is edited this time.

Only the affected section is read; unchanged snapshots are not saved. A read
returns the note entries that section owns, keyed exactly as the injector's getQuestions
keys them (Stride: the section itself; Prompt: each question in it), and
NoteStore merges a note one top-level key at a time, so the rest of the note
keeps its values.

A read is saved only under the note it was read from. The note key must be
the same before and after the read, and after a switch between notes nothing
is read until the page stops showing what it showed for the previous note
(getNoteStamp), since an SPA can name the next note in the URL while the last
one is still on screen.
*/

const DEBOUNCE_MS = 1000;
const MAX_WAIT_MS = 10 * 1000;
const IDLE_CAPTURE_TIMEOUT_MS = 250;
// NoteOffload uploads a note once it has gone this long without changing.
const UPLOAD_CHECK_DELAY_MS = 5 * 60 * 1000 + 1000;

export type SectionRead = {
  // The note's top-level entries that belong to this section.
  values: Record<string, any>;
  // Paths (from the note's root) that exist but cannot be read right now,
  // such as a collapsed card: their last captured values are kept.
  keep?: (string | number)[][];
};

export type FocusCaptureSource = {
  // These run on every focus change and mutation batch: cheap and read-only.
  getNoteKey: () => string | null;
  sectionOf: (node: Node) => string | null;
  listSections: () => string[];
  // Changes when the page shows a different note. Compared with ===.
  getNoteStamp: () => unknown;
  readSection: (section: string) => SectionRead | null;
  getPatientInfo: () => any;
  getPageInfo: () => any;
  // Attributes through which this EMR's custom controls show a new value.
  observedAttributes: string[];
  // Whether this click or form submit signs the note off. Each EMR decides;
  // the capture and sign-off that follow are shared. Read-only.
  isFinalization?: (event: Event) => boolean;
};

let source: FocusCaptureSource | null = null;
let emrKey: string | null = null;

// The note on screen and its sections' state. Reset whenever the note changes.
let noteKey: string | null = null;
let observer: MutationObserver | null = null;
const dirtySections = new Set<string>();
const lastSaved = new Map<string, string>();
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingSince: number | null = null;
const idleSections = new Set<string>();
let idleScheduled = false;
// What the page showed for the attached note, and for the note before it while
// the page may still be showing that one.
let attachedStamp: { value: unknown } | null = null;
let previousStamp: { value: unknown } | null = null;

let saveQueue: Promise<void> = Promise.resolve();
let uploadTimer: ReturnType<typeof setTimeout> | null = null;
let savedSinceUploadCheck = false;

const DOCUMENT_LISTENERS: Array<[string, (event: any) => void]> = [
  ['focusout', onFocusOut],
  ['input', onInput],
  ['click', onFinalization],
  ['submit', onFinalization],
  ['visibilitychange', onVisibilityChange]
];
const WINDOW_LISTENERS: Array<[string, (event: any) => void]> = [
  ['popstate', checkNote],
  ['pagehide', onPageHide]
];

/** Start, retarget or stop capture. A null source or emrKey stops it. */
export function syncFocusNoteCapture(
  nextSource: FocusCaptureSource | null | undefined,
  nextEmrKey: string | null
) {
  if (!nextSource || !nextEmrKey || !hasNoteStorage()) {
    stopFocusNoteCapture();
    return;
  }
  if (nextSource === source && nextEmrKey === emrKey) return;

  stopFocusNoteCapture();
  source = nextSource;
  emrKey = nextEmrKey;
  DOCUMENT_LISTENERS.forEach(([type, listener]) =>
    document.addEventListener(type, listener, true)
  );
  WINDOW_LISTENERS.forEach(([type, listener]) =>
    window.addEventListener(type, listener, true)
  );
  // Chrome's Navigation API: 'navigate' fires before a same-document URL
  // change, while the DOM still shows the note being left.
  const navigation = (window as any).navigation;
  navigation?.addEventListener?.('navigate', onNavigate);
  navigation?.addEventListener?.('currententrychange', checkNote);
  syncNote();
  void flushNotes();
}

export function stopFocusNoteCapture() {
  if (!source) return;
  detachNote();
  DOCUMENT_LISTENERS.forEach(([type, listener]) =>
    document.removeEventListener(type, listener, true)
  );
  WINDOW_LISTENERS.forEach(([type, listener]) =>
    window.removeEventListener(type, listener, true)
  );
  const navigation = (window as any).navigation;
  navigation?.removeEventListener?.('navigate', onNavigate);
  navigation?.removeEventListener?.('currententrychange', checkNote);
  if (uploadTimer) clearTimeout(uploadTimer);
  uploadTimer = null;
  source = null;
  emrKey = null;
}

/** Attach to the note now on screen, or detach when there is none. */
function syncNote() {
  if (!source) return false;
  const current = safely(() => source!.getNoteKey(), null);
  if (current === noteKey) return noteKey !== null;

  const leaving = noteKey ? attachedStamp : null;
  detachNote();
  if (!current) return false;
  noteKey = current;
  previousStamp = leaving;
  showsAttachedNote();
  observer = new MutationObserver(onMutations);
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeOldValue: true,
    attributeFilter: source.observedAttributes
  });
  // The first read of every section already rendered.
  listSections().forEach(markDirty);
  return true;
}

function detachNote() {
  observer?.disconnect();
  observer = null;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = null;
  pendingSince = null;
  dirtySections.clear();
  idleSections.clear();
  lastSaved.clear();
  noteKey = null;
  attachedStamp = null;
  previousStamp = null;
}

/** False while the page still shows the note we switched away from. */
function showsAttachedNote() {
  let stamp: unknown;
  try {
    stamp = source?.getNoteStamp();
  } catch (_) {
    return false;
  }
  if (previousStamp) {
    if (stamp === previousStamp.value) return false;
    previousStamp = null;
  }
  attachedStamp = { value: stamp };
  return true;
}

/* URL changes are how a newly opened note is noticed. */
function checkNote() {
  syncNote();
}

function onInput(event: Event) {
  if (!syncNote() || !(event.target instanceof Node)) return;
  const section = sectionOf(event.target);
  if (section && section !== focusedSection()) markDirty(section);
}

function onFocusOut(event: FocusEvent) {
  if (!syncNote() || !(event.target instanceof Node)) return;
  const section = sectionOf(event.target);
  if (section) captureWhenIdle(section);
}

function onFinalization(event: Event) {
  if (event.defaultPrevented) return;
  if (
    !safely(() => source?.isFinalization?.(event) ?? false, false) ||
    !syncNote() || !emrKey || !noteKey || !showsAttachedNote()
  ) return;

  // Read before the EMR's click handler can close the note. Keep this identity
  // across queued saves, even if the clinician immediately opens another visit.
  const signedEmrKey = emrKey;
  const signedNoteKey = noteKey;
  const signedOffAt = Date.now();
  captureSections(listSections());
  saveQueue = saveQueue.then(async () => {
    await markCapturedNoteSignedOff(signedEmrKey, signedNoteKey, signedOffAt);
    await flushDueEntries(true);
  }).catch(() => {});
}

function onMutations(records: MutationRecord[]) {
  if (!syncNote()) return;
  const touched = new Set<string>();
  let addedOutsideSections = false;
  records.forEach((record) => {
    if (isRippleMutation(record) || isUnchangedAttribute(record)) return;
    const section = sectionOf(record.target);
    if (section) touched.add(section);
    record.addedNodes.forEach((node) => {
      const added = sectionOf(node);
      if (added) touched.add(added);
      else if (!section && node.nodeType === Node.ELEMENT_NODE) {
        addedOutsideSections = true;
      }
    });
  });
  // A container holding whole sections was added: give any section not read
  // yet its first read.
  if (addedOutsideSections) {
    listSections()
      .filter((section) => !lastSaved.has(section))
      .forEach((section) => touched.add(section));
  }
  touched.forEach(markDirty);
}

// MUI adds and removes ripple spans on every click; they never carry a value.
function isRippleMutation(record: MutationRecord) {
  const target =
    record.target instanceof Element ? record.target : record.target.parentElement;
  return record.type === 'childList' && Boolean(target?.closest('.MuiTouchRipple-root'));
}

// A re-render (Stride's autosave) writes the same value attribute back.
function isUnchangedAttribute(record: MutationRecord) {
  return (
    record.type === 'attributes' &&
    record.target instanceof Element &&
    record.oldValue === record.target.getAttribute(record.attributeName!)
  );
}

function markDirty(section: string) {
  dirtySections.add(section);
  // The focusout that ends this edit will read it.
  if (section === focusedSection()) return;

  const now = Date.now();
  if (pendingSince === null) pendingSince = now;
  if (debounceTimer) clearTimeout(debounceTimer);
  const wait = Math.max(0, Math.min(DEBOUNCE_MS, pendingSince + MAX_WAIT_MS - now));
  debounceTimer = setTimeout(onQuiet, wait);
}

function onQuiet() {
  debounceTimer = null;
  pendingSince = null;
  if (!syncNote()) return;
  const focused = focusedSection();
  captureSections(Array.from(dirtySections).filter((section) => section !== focused));
}

/**
 * Read in the next idle period: after the focus change has painted, and
 * before a click that navigates away has finished (that takes a mouseup).
 */
function captureWhenIdle(section: string) {
  idleSections.add(section);
  if (idleScheduled) return;
  idleScheduled = true;
  const run = () => {
    idleScheduled = false;
    const sections = Array.from(idleSections);
    idleSections.clear();
    if (syncNote()) captureSections(sections);
  };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: IDLE_CAPTURE_TIMEOUT_MS });
  } else {
    setTimeout(run, 0);
  }
}

/* Fired before a same-document URL change, while the DOM is still this note's. */
function onNavigate() {
  if (noteKey) captureSections(Array.from(new Set([...dirtySections, ...idleSections])));
}

/*
The tab is closing or being hidden. Read what is pending, including the
section being typed in, whose focusout may never come.
*/
function capturePendingAndFocused() {
  const sections = new Set([...dirtySections, ...idleSections]);
  const focused = focusedSection();
  if (focused) sections.add(focused);
  captureSections(Array.from(sections));
}

function onPageHide() {
  if (!source) return;
  if (syncNote()) capturePendingAndFocused();
  saveQueue = saveQueue.then(() => flushDueEntries(true)).catch(() => {});
}

function onVisibilityChange() {
  if (document.visibilityState === 'hidden' && syncNote()) capturePendingAndFocused();
}

function focusedSection() {
  const active = document.activeElement;
  if (!active || active === document.body || !document.hasFocus()) return null;
  return sectionOf(active);
}

function sectionOf(node: Node) {
  return safely(() => source?.sectionOf(node) ?? null, null);
}

function listSections() {
  return safely(() => source?.listSections() ?? [], []);
}

function captureSections(sections: string[]) {
  if (sections.length === 0 || !hasNoteStorage()) return;
  // Stay dirty until the page shows this note.
  if (!showsAttachedNote()) return;
  sections.forEach(captureSection);
}

/** Read one section now and queue its save. */
function captureSection(section: string) {
  if (!source || !emrKey || !noteKey) return;
  dirtySections.delete(section);

  let read: SectionRead | null = null;
  let patientInfo: any;
  let pageInfo: any;
  try {
    if (source.getNoteKey() !== noteKey) return;
    read = source.readSection(section);
    patientInfo = source.getPatientInfo();
    pageInfo = source.getPageInfo();
    if (source.getNoteKey() !== noteKey) return;
  } catch (_) {
    return;
  }
  if (!read) return;
  const sectionRead = read;

  // Most reads find the section exactly as it was last saved.
  const snapshot = JSON.stringify([sectionRead.values, patientInfo, pageInfo]);
  if (lastSaved.get(section) === snapshot) return;
  lastSaved.set(section, snapshot);

  const savedEmrKey = emrKey;
  const savedNoteKey = noteKey;
  saveQueue = saveQueue
    .then(() =>
      saveSection(savedEmrKey, savedNoteKey, sectionRead, patientInfo, pageInfo)
    )
    .catch(() => {
      // Let the next read try again.
      if (lastSaved.get(section) === snapshot) lastSaved.delete(section);
    });
}

async function saveSection(
  savedEmrKey: string,
  savedNoteKey: string,
  read: SectionRead,
  patientInfo: any,
  pageInfo: any
) {
  const values = await withKeptValues(savedEmrKey, savedNoteKey, read);
  await saveCapturedNote(savedEmrKey, patientInfo, pageInfo, savedNoteKey, values);
  scheduleUploadCheck();
}

/**
 * Every section of the note on screen, read now, for linking a Comprehend run
 * to the note (NoteCapture's linkNoteToConversation). Null unless the same
 * note is on screen before and after the read.
 */
export async function readFocusCaptureNote(
  noteSource: FocusCaptureSource,
  noteEmrKey: string
) {
  const key = noteSource.getNoteKey();
  if (!key) return null;
  const read: Required<SectionRead> = { values: {}, keep: [] };
  noteSource.listSections().forEach((section) => {
    const sectionRead = noteSource.readSection(section);
    Object.assign(read.values, sectionRead?.values);
    read.keep.push(...(sectionRead?.keep ?? []));
  });
  const patientInfo = noteSource.getPatientInfo();
  const pageInfo = noteSource.getPageInfo();
  if (noteSource.getNoteKey() !== key) return null;
  const questions = await withKeptValues(noteEmrKey, key, read);
  if (Object.keys(questions).length === 0) return null;
  return { noteKey: key, patientInfo, pageInfo, questions };
}

/**
 * A read's values plus the stored value of every kept path the read lacks.
 * Only paths inside an entry the read replaces need this: NoteStore already
 * keeps entries a read leaves out.
 */
async function withKeptValues(
  storeEmrKey: string,
  storeNoteKey: string,
  { values, keep }: SectionRead
) {
  const paths = (keep ?? []).filter(
    (path) => path.length > 1 && path[0] in values && valueAt(values, path) === undefined
  );
  if (paths.length === 0) return values;
  const store = await getCapturedNotes();
  const stored = store.entries[getNoteCacheKey(storeEmrKey, storeNoteKey)]?.questions;
  const merged = JSON.parse(JSON.stringify(values));
  paths.forEach((path) => {
    const previous = valueAt(stored, path);
    if (previous === undefined) return;
    let node = merged;
    path.slice(0, -1).forEach((segment) => {
      if (node[segment] === null || typeof node[segment] !== 'object') node[segment] = {};
      node = node[segment];
    });
    node[path[path.length - 1]] = previous;
  });
  return merged;
}

function valueAt(root: any, path: (string | number)[]) {
  let node = root;
  for (const segment of path) {
    if (node === null || typeof node !== 'object') return undefined;
    node = node[segment];
  }
  return node;
}

/*
Check at most this often while saves keep arriving, and once after the last.
Resetting the timer on every save would never flush during steady charting,
and NoteStore keeps only the newest notes, uploaded or not.
*/
function scheduleUploadCheck() {
  if (!source) return;
  savedSinceUploadCheck = true;
  if (uploadTimer) return;
  savedSinceUploadCheck = false;
  uploadTimer = setTimeout(() => {
    uploadTimer = null;
    void flushNotes();
    if (savedSinceUploadCheck) scheduleUploadCheck();
  }, UPLOAD_CHECK_DELAY_MS);
}

async function flushNotes() {
  if (!source || !hasNoteStorage()) return;
  try {
    await flushDueEntries();
  } catch (_) {
    return;
  }
}

function safely<T>(read: () => T, fallback: T): T {
  try {
    return read();
  } catch (_) {
    return fallback;
  }
}
